﻿import { useEffect, useMemo, useState } from "react";
import { useLocalStorage, useDebounce } from "@/hooks";
import { ChevronDown, ChevronRight, RefreshCw, Search, LayoutList } from "lucide-react";
import AsyncSelect from "react-select/async";
import { EngagementPeriod, type EngagementEventType, type EngagementFilters } from "@/types/engagement";
import type { SelectOption } from "@/components/common/Dropdown/types";
import { groupKey, groupLabel, groupDateRange, periodToUtcRange } from "@/utils/dateUtils";
import { PERIODS, TYPE_FILTER_PILLS, TYPE_CONFIG, PAGE_SIZE } from "./engagementConfig";
import { engagementEventKey, groupEventsByPeriod } from "./engagementUtils";
import { ENGAGEMENT_SELECT_STYLES, loadCompanyOptions, loadUserOptions } from "./engagementSelectStyles";
import { EngagementEventCard } from "./EngagementEventCard";
import { useEngagementFeed } from "./useEngagementFeed";
import { useEngagementSummary } from "./useEngagementSummary";

type Props = {
  filters: Pick<EngagementFilters, "companyIds" | "userIds">;
  defaultUsers?: SelectOption[];
};

export const EngagementWidget = ({ filters, defaultUsers }: Props) => {
  const { getCacheItem, updateCacheItem } = useLocalStorage();
  const [selectedTypes, setSelectedTypes] = useState<Set<EngagementEventType>>(() => {
    const cached = getCacheItem<EngagementEventType[]>("engagement_types");
    return cached ? new Set(cached) : new Set();
  });
  const [period, setPeriod] = useState<EngagementPeriod>(() => {
    const cached = getCacheItem<EngagementPeriod>("engagement_period");
    return cached && Object.values(EngagementPeriod).includes(cached)
      ? cached
      : EngagementPeriod.OneMonth;
  });
  const toggleType = (type: EngagementEventType) => setSelectedTypes(prev => {
    const next = new Set(prev);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    return next;
  });
  const [selectedUsers, setSelectedUsers] = useState<SelectOption[]>(defaultUsers ?? []);
  const [selectedCompanies, setSelectedCompanies] = useState<SelectOption[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 400, 3);
  const [viewMode, setViewMode] = useState<"list" | "grouped">(
    getCacheItem<"list" | "grouped">("engagement_viewMode") ?? "list"
  );
  const [expandedTypeKeys, setExpandedTypeKeys] = useState<Set<string>>(new Set());
  const effectiveCompanyIds = useMemo(
    () => selectedCompanies.length > 0 ? selectedCompanies.map(company => company.value) : filters.companyIds,
    [filters.companyIds, selectedCompanies]
  );
  const currentFilters = useMemo<EngagementFilters>(() => {
    const range = periodToUtcRange(period);
    return {
      companyIds: effectiveCompanyIds,
      userIds: selectedUsers.length > 0 ? selectedUsers.map(user => user.value) : filters.userIds,
      ...range,
      types: selectedTypes.size === 0 ? undefined : [...selectedTypes],
      search: debouncedSearch || undefined,
    };
  }, [debouncedSearch, effectiveCompanyIds, filters.userIds, period, selectedTypes, selectedUsers]);
  const {
    items, nextCursor, hasMore, loading, loadingMore, error: feedError,
    load: loadFeed, reset: resetFeed,
  } = useEngagementFeed();
  const {
    groups: summaryGroups, loading: summaryLoading, error: summaryError,
    typeItems, typeCursors, typeHasMore, typeLoading, typeErrors,
    loadSummary, loadTypeItems: loadSummaryItems, reset: resetSummary,
  } = useEngagementSummary();
  const showCompany = !filters.companyIds?.length || filters.companyIds.length > 1;

  const loadTypeItems = (itemKey: string, type: EngagementEventType, from: string, to: string, cursor: string | null, append: boolean) => {
    void loadSummaryItems(itemKey, {
      ...currentFilters,
      from,
      to,
      types: [type],
      cursor: cursor ?? undefined,
      pageSize: 50,
    }, append);
  };

  const toggleTypeGroup = (key: string, type: EngagementEventType, from: string, to: string) => {
    if (!expandedTypeKeys.has(key) && !typeItems.has(key)) {
      loadTypeItems(key, type, from, to, null, false);
    }
    setExpandedTypeKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    updateCacheItem("engagement_viewMode", viewMode);
  }, [viewMode, updateCacheItem]);

  useEffect(() => {
    updateCacheItem("engagement_period", period);
  }, [period, updateCacheItem]);

  useEffect(() => {
    updateCacheItem("engagement_types", [...selectedTypes]);
  }, [selectedTypes, updateCacheItem]);

  useEffect(() => {
    resetFeed();
    resetSummary();
    setExpandedTypeKeys(new Set());
    if (viewMode === "list") {
      void loadFeed({ ...currentFilters, pageSize: PAGE_SIZE });
    } else {
      void loadSummary(currentFilters);
    }
  }, [currentFilters, viewMode, loadFeed, loadSummary, resetFeed, resetSummary]);

  const handleLoadMore = () => {
    void loadFeed({
      ...currentFilters,
      cursor: nextCursor ?? undefined,
      pageSize: PAGE_SIZE,
    }, true);
  };

  const groups = groupEventsByPeriod(items);
  const isLoading = loading || summaryLoading;
  const error = viewMode === "list" ? feedError : summaryError;

  return (
    <div>
      {/* Row 1: keyword search + user filter */}
      <div className="flex flex-col sm:flex-row border-b border-gray-200">
        <div className="relative flex items-center px-4 py-2.5 border-b border-gray-200 sm:flex-3 sm:border-b-0 sm:border-r">
          <Search size={14} className="absolute left-6.75 text-gray-400 pointer-events-none" />
          <input
            type="search"
            disabled={isLoading}
            className="w-full py-2.5 pl-8 pr-2.5 min-h-0 text-[14px] border border-gray-300 rounded-md bg-gray-50 text-gray-800 outline-none focus:border-blue-500 focus:bg-white placeholder:text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        {!filters.companyIds?.length && (
          <div className="flex items-center px-3 py-1.5 border-b border-gray-200 sm:flex-3 sm:border-b-0 sm:border-r">
            <AsyncSelect<SelectOption, true>
              isDisabled={isLoading}
              isMulti
              loadOptions={loadCompanyOptions}
              defaultOptions
              onChange={options => setSelectedCompanies([...(options ?? [])])}
              value={selectedCompanies}
              isClearable
              placeholder="All companies"
              noOptionsMessage={() => "No companies found"}
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              menuPosition="fixed"
              styles={ENGAGEMENT_SELECT_STYLES}
              className="w-full"
              closeMenuOnSelect={false}
            />
          </div>
        )}
        <div className="flex items-center px-3 py-1.5 sm:flex-3">
          <AsyncSelect<SelectOption, true>
            isDisabled={isLoading}
            isMulti
            loadOptions={loadUserOptions}
            onChange={options => setSelectedUsers([...(options ?? [])])}
            value={selectedUsers}
            isClearable
            placeholder="All users"
            noOptionsMessage={({ inputValue }) => inputValue.length < 2 ? "Type 2+ characters..." : "No users found"}
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
            styles={ENGAGEMENT_SELECT_STYLES}
            className="w-full"
            closeMenuOnSelect={false}
          />
        </div>
      </div>

      {/* Row 2: type filter pills + period/view controls */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-y-2 px-4 py-2 border-b border-gray-200">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={`inline-flex items-center justify-center px-3 py-1.5 text-sm leading-none font-medium rounded-full border cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              selectedTypes.size === 0
                ? "bg-gray-800 border-gray-800 text-white"
                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-100"
            }`}
            onClick={() => setSelectedTypes(new Set())}
            disabled={isLoading}
          >
            All
          </button>
          {TYPE_FILTER_PILLS.map(({ key, label }) => {
            const cfg = TYPE_CONFIG[key];
            const active = selectedTypes.has(key);
            return (
              <button
                key={key}
                type="button"
                className="inline-flex items-center justify-center px-3 py-1.5 text-sm leading-none font-medium rounded-full border cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={active
                  ? { backgroundColor: cfg.color, borderColor: cfg.color, color: "#fff" }
                  : { backgroundColor: "#fff", borderColor: "#d1d5db", color: "#6b7280" }}
                onClick={() => toggleType(key)}
                disabled={isLoading}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-x-0.5 gap-y-1.5 sm:ml-auto">
          <div className="flex gap-1.5 mr-2">
            {PERIODS.map(p => (
              <button
                key={p.key}
                type="button"
                className={`inline-flex items-center justify-center px-3 py-1.5 text-xs leading-none font-medium border rounded-full cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  period === p.key
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100"
                }`}
                onClick={() => { if (p.key !== period) setPeriod(p.key); }}
                disabled={isLoading}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={isLoading}
            className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs leading-none font-medium border rounded-full cursor-pointer transition-colors mr-1 disabled:opacity-40 disabled:cursor-not-allowed ${
              viewMode === "grouped"
                ? "bg-blue-600 border-blue-600 text-white"
                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setViewMode(v => v === "list" ? "grouped" : "list")}
          >
            <LayoutList size={11} />
            Group
          </button>
          <button
            type="button"
            disabled={isLoading}
            className="bg-transparent border-0 text-gray-400 cursor-pointer px-1.5 py-1 leading-none hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => {
              if (viewMode === "list") {
                void loadFeed({ ...currentFilters, pageSize: PAGE_SIZE });
              } else {
                void loadSummary(currentFilters);
              }
            }}
            title="Refresh"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {(viewMode === "list" ? loading : summaryLoading) && (
        <div className="flex flex-col gap-2 px-4 pt-3">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-18 rounded-lg bg-gray-100 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && !summaryLoading && error && (
        <p className="text-gray-400 text-[14px] py-8 px-4 text-center">{error}</p>
      )}

      {viewMode === "list" && !loading && !error && items.length === 0 && (
        <p className="text-gray-400 text-[14px] py-8 px-4 text-center">No activity in this period.</p>
      )}
      {viewMode === "grouped" && !summaryLoading && !error && summaryGroups.length === 0 && (
        <p className="text-gray-400 text-[14px] py-8 px-4 text-center">No activity in this period.</p>
      )}

      {viewMode === "list" && !loading && groups.length > 0 && (
        <div className="flex flex-col bg-gray-50 px-4 pt-3 pb-4">
          {groups.map(group => (
            <div key={group.label} className="mt-3 first:mt-0">
              <div className="text-[14px] font-bold text-gray-600 uppercase tracking-wide pt-4 pb-2 px-0.5 border-b border-gray-300 mb-2">{group.label}</div>
              {group.events.map(event => <EngagementEventCard key={engagementEventKey(event)} event={event} showCompany={showCompany} />)}
            </div>
          ))}
          {hasMore && (
            <button
              type="button"
              className="flex items-center justify-center gap-1.5 w-full mt-2.5 py-3 text-sm font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-lg cursor-pointer hover:bg-sky-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading..." : <><ChevronDown size={13} /> Load more</>}
            </button>
          )}
        </div>
      )}

      {viewMode === "grouped" && !summaryLoading && summaryGroups.length > 0 && (() => {
        // Group per-day summary entries into named periods, aggregating counts for the same type
        const periodMap = new Map<string, { label: string; from: string; to: string; types: { type: EngagementEventType; count: number }[] }>();
        for (const s of summaryGroups) {
          const pKey = groupKey(s.date);
          if (!periodMap.has(pKey)) periodMap.set(pKey, { label: groupLabel(pKey), ...groupDateRange(pKey), types: [] });
          const p = periodMap.get(pKey)!;
          const type = s.entityType as EngagementEventType;
          const existing = p.types.find(t => t.type === type);
          if (existing) existing.count += s.count;
          else p.types.push({ type, count: s.count });
        }
        return (
          <div className="flex flex-col bg-gray-50 px-4 pt-3 pb-4">
            {Array.from(periodMap.values()).map(p => (
              <div key={p.label} className="mt-3 first:mt-0">
                <div className="text-[14px] font-bold text-gray-600 uppercase tracking-wide pt-4 pb-2 px-0.5 border-b border-gray-300 mb-2">{p.label}</div>
                {p.types.map(({ type, count }) => {
                  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.note;
                  const TypeIcon = cfg.icon;
                  const itemKey = `${p.label}:${type}`;
                  const isExpanded = expandedTypeKeys.has(itemKey);
                  const Chevron = isExpanded ? ChevronDown : ChevronRight;
                  const loadedItems = typeItems.get(itemKey) ?? [];
                  const isLoadingType = typeLoading.has(itemKey);
                  const hasTypeError = typeErrors.has(itemKey);
                  const canLoadMore = typeHasMore.get(itemKey) ?? false;
                  const typeCursor = typeCursors.get(itemKey) ?? null;
                  return (
                    <div key={type} className="mb-2">
                      <button
                        type="button"
                        className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 text-left"
                        style={{ boxShadow: `inset 5px 0 0 ${cfg.color}` }}
                        onClick={() => toggleTypeGroup(itemKey, type, p.from, p.to)}
                      >
                        <span className="text-[11px] text-gray-400">{count}</span>
                        <Chevron size={13} className="text-gray-400 shrink-0" />
                        <span className="shrink-0 flex items-center justify-center w-6 h-6 rounded-full" style={{ color: cfg.color, backgroundColor: cfg.backgroundColor ?? cfg.color + '18' }}>
                          <TypeIcon size={13} />
                        </span>
                        <span className="flex-1 text-[14px] font-semibold text-gray-700">{cfg.label}</span>
                      </button>
                      {isExpanded && (
                        <div className="mt-1.5 ml-2">
                          {isLoadingType && !loadedItems.length && (
                            <div className="flex flex-col gap-1.5 py-1">
                              {[0, 1].map(i => <div key={i} className="h-14 rounded-lg bg-gray-100 animate-pulse" />)}
                            </div>
                          )}
                          {hasTypeError && (
                            <div className="flex items-center justify-between gap-3 py-2 px-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
                              <span>Failed to load {cfg.label.toLowerCase()} items.</span>
                              <button
                                type="button"
                                className="font-medium text-red-700 bg-transparent border-0 cursor-pointer hover:underline"
                                onClick={() => loadTypeItems(itemKey, type, p.from, p.to, typeCursor, loadedItems.length > 0)}
                              >
                                Retry
                              </button>
                            </div>
                          )}
                          {loadedItems.map(event => <EngagementEventCard key={engagementEventKey(event)} event={event} showCompany={showCompany} />)}
                          {canLoadMore && !hasTypeError && (
                            <button
                              type="button"
                              className="flex items-center justify-center gap-1.5 w-full mt-1.5 py-3 text-sm font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-lg cursor-pointer hover:bg-sky-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              onClick={() => loadTypeItems(itemKey, type, p.from, p.to, typeCursor, true)}
                              disabled={isLoadingType}
                            >
                              {isLoadingType ? "Loading..." : <><ChevronDown size={13} /> Load more</>}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}






